import { Component, OnInit, Input, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, NavigationEnd, Router, RouterModule } from '@angular/router';
import { filter, map } from 'rxjs/operators';
import { RolesApiService } from '@angular-workspace/services';

@Component({
  selector: 'app-page-header',
  standalone: true,
  imports: [CommonModule, RouterModule],
  templateUrl: './page-header.component.html',
  styleUrl: './page-header.component.scss',
})
export class PageHeaderComponent implements OnInit {
  title: string = '';
  description: string = '';
  
  @Input() variant: 'default' | 'compact-inline' = 'compact-inline';

  private rolesService = inject(RolesApiService);

  constructor(
    private route: ActivatedRoute,
    private router: Router
  ) {}

  ngOnInit(): void {
    // Get initial route data - use setTimeout to ensure route is fully initialized
    setTimeout(() => {
      this.updateHeaderData();
    }, 0);

    // Update on route changes
    this.router.events
      .pipe(
        filter(event => event instanceof NavigationEnd)
      )
      .subscribe(() => {
        setTimeout(() => {
          this.updateHeaderData();
        }, 0);
      });
  }

  private updateHeaderData(): void {
    // Get the deepest child route (current route)
    let currentRoute = this.route;
    while (currentRoute.firstChild) {
      currentRoute = currentRoute.firstChild;
    }

    // Try to get data from the deepest child route first
    let title = currentRoute.snapshot.data['title'] || '';
    let description = currentRoute.snapshot.data['description'] || '';

    // If not found, traverse up the route tree to find data
    if (!title || !description) {
      let route: ActivatedRoute | null = currentRoute;
      while (route) {
        const data = route.snapshot.data;
        if (data && data['title'] && !title) {
          title = data['title'];
        }
        if (data && data['description'] && !description) {
          description = data['description'];
        }
        route = route.parent;
      }
    }

    // Fallback: check root route
    if (!title || !description) {
      const rootData = this.route.root.snapshot.data;
      if (rootData && rootData['title'] && !title) {
        title = rootData['title'];
      }
      if (rootData && rootData['description'] && !description) {
        description = rootData['description'];
      }
    }

    this.title = title;
    
    // Replace "Welcome !!" with dynamic role-based message
    if (description === 'Welcome !!') {
      this.updateDescriptionWithRole();
    } else {
      this.description = description;
    }
  }

  private async updateDescriptionWithRole(): Promise<void> {
    try {
      await this.rolesService.getRoles();
      const userRoles = this.rolesService.getUserRoles();
      
      if (userRoles && userRoles.length > 0) {
        const highestRole = this.rolesService.getHighestRole(userRoles);
        
        if (highestRole) {
          // Capitalize first letter and format role name
          const formattedRole = highestRole.charAt(0).toUpperCase() + highestRole.slice(1).toLowerCase();
          this.description = `Welcome to ${formattedRole} Panel !!`;
        } else {
          this.description = 'Welcome !!';
        }
      } else {
        this.description = 'Welcome !!';
      }
    } catch (error) {
      console.error('Error getting highest role for welcome message:', error);
      this.description = 'Welcome !!';
    }
  }
}

